import React, { useState, useMemo, useRef, useEffect, useCallback, useLayoutEffect } from "react";
import { CHART_COLORS_HEX, hexToRgba } from "../../lib/colorSystem";
import { toDisplayCurrency } from "../../utils/formatters";
import { useCurrencyMode } from "../../context/CurrencyContext";

// =============================================================================
// FINANCIAL GRAPH
// =============================================================================
// PURPOSE: Revenue/cash area chart with daily and cumulative modes, custom tooltip showing values on hover.
//
// REQUIREMENTS:
// - Daily mode: Shows daily cash/revenue bars
// - Cumulative mode: Running total with data labels (thinned to ~6 labels to avoid overlap)
// - Toggle between modes via button
// - Responsive chart container
// - Custom tooltip shows both revenue and cash on hover
//
// KEY DECISIONS:
// - Cumulative mode uses step calculation to show ~6 labels max (prevents label crowding)
// - Daily mode thins labels by PIXEL BUCKET (SLOT_LABEL_WIDTH): the x-axis is sliced
//   into ~56px slots and only the highest point in each slot gets a label. This keeps the
//   meaningful peaks legible at wide ranges (165 days) while showing every label at narrow
//   ranges (7 days), all without a count-based special case. Pixels-per-day was the root
//   cause of the old "labels collapse into soup past ~40 days" bug.
// - Custom SVG implementation — NO recharts dependency (saves 327KB)
// - Month abbreviation formatting (Jan, Feb, etc.)
// =============================================================================

interface ChartDataPoint {
  date: string;
  cash: number;
  revenue: number;
  label?: string;
  revenueLabel?: string;
}

interface FinancialGraphProps {
  data: ChartDataPoint[];
  showRevenue?: boolean;
  showCash?: boolean;
  /** Monthly revenue goal — used to show daily pace line */
  goalAmount?: number;
  /** Total days in the current month */
  daysInMonth?: number;
  /**
   * Resolution of the per-period series. Set by the parent when it has rolled the data
   * up for a long range (daily -> weekly -> monthly). Only relabels the per-period toggle
   * button so the UI is honest ("Monthly" not "Daily"); rendering is unchanged. Default "daily".
   */
  granularity?: "daily" | "weekly" | "monthly";
}

const GRANULARITY_LABEL: Record<NonNullable<FinancialGraphProps["granularity"]>, string> = {
  daily: "Daily",
  weekly: "Weekly",
  monthly: "Monthly",
};

function formatCompactValue(val: number): string {
  const d = toDisplayCurrency(val); // money-only (cash/revenue) — exec USD->AUD toggle
  if (d >= 1000) return `${(d / 1000).toFixed(1).replace(/\.0$/, "")}K`;
  return d.toFixed(0);
}

const MONTH_NAMES = [
  "Jan",
  "Feb",
  "Mar",
  "Apr",
  "May",
  "Jun",
  "Jul",
  "Aug",
  "Sep",
  "Oct",
  "Nov",
  "Dec",
];

function formatDateLabel(d: string): string {
  const parts = d.split("/");
  if (parts.length >= 2) {
    const m = parseInt(parts[0], 10) - 1;
    return `${MONTH_NAMES[m] || parts[0]} ${parts[1]}`;
  }
  return d;
}

const PADDING = { top: 25, right: 30, bottom: 25, left: 45 };

// Daily-mode label thinning: minimum horizontal spacing (px) between data labels.
// The plotted x-range is sliced into slots this wide and only the highest-value point
// in each slot keeps its label — peaks stay legible, troughs go quiet. Pixel-based (not
// count-based) so narrow ranges where every day already has room keep all their labels.
const SLOT_LABEL_WIDTH = 56;

// Bucket point indices into SLOT_LABEL_WIDTH-wide horizontal slots and keep only the
// index with the largest value in each slot. Returns the Set of indices to label.
// Exported for unit testing (see __tests__/financialGraph.labels.test.ts).
export function pickPeakLabelIndices(
  points: { x: number; y: number }[],
  values: number[]
): Set<number> {
  const keep = new Set<number>();
  const bestInSlot = new Map<number, { idx: number; val: number }>();
  for (let i = 0; i < points.length; i++) {
    if (!(values[i] > 0)) continue;
    const slot = Math.floor((points[i].x - PADDING.left) / SLOT_LABEL_WIDTH);
    const cur = bestInSlot.get(slot);
    if (!cur || values[i] > cur.val) bestInSlot.set(slot, { idx: i, val: values[i] });
  }
  bestInSlot.forEach(({ idx }) => keep.add(idx));
  return keep;
}

interface TooltipState {
  visible: boolean;
  x: number;
  y: number;
  index: number;
}

const FinancialGraph: React.FC<FinancialGraphProps> = ({
  data,
  showRevenue: canShowRevenue = true,
  showCash: canShowCash = true,
  goalAmount: _goalAmount = 0,
  daysInMonth: _daysInMonth = 0,
  granularity = "daily",
}) => {
  // Subscribes this React.memo'd component to currency flips AND is a dep of the
  // chartData useMemo below so the on-chart cash/revenue labels recompute (not
  // just the inline axis/tooltip). NB: local `mode` = daily/cumulative, distinct.
  const currencyMode = useCurrencyMode();
  const [mode, setMode] = useState<"daily" | "cumulative">("daily");
  const [cashVisible, setCashVisible] = useState(true);
  const [revenueVisible, setRevenueVisible] = useState(true);

  const containerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<ResizeObserver | null>(null);
  const [dims, setDims] = useState({ width: 0, height: 0 });
  const [tooltip, setTooltip] = useState<TooltipState>({ visible: false, x: 0, y: 0, index: -1 });

  // Callback ref + useLayoutEffect: fires every time the node attaches AND
  // measures synchronously after layout but before paint. This avoids the
  // blank-on-cold-mount bug where useRef + useEffect([]) would read 0×0
  // before the parent flex container had computed its size, then never
  // re-measure when data arrived (route-bounce was the only recovery).
  const setContainerNode = useCallback((node: HTMLDivElement | null) => {
    if (observerRef.current) {
      observerRef.current.disconnect();
      observerRef.current = null;
    }
    containerRef.current = node;
    if (!node) return;
    const ro = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (entry) {
        setDims({ width: entry.contentRect.width, height: entry.contentRect.height });
      }
    });
    ro.observe(node);
    observerRef.current = ro;
  }, []);

  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const w = el.clientWidth;
    const h = el.clientHeight;
    if (w > 0 && h > 0 && (w !== dims.width || h !== dims.height)) {
      setDims({ width: w, height: h });
    }
  });

  useEffect(() => () => observerRef.current?.disconnect(), []);

  const chartData = useMemo(() => {
    if (mode === "daily") {
      return data.map((d) => ({
        ...d,
        label: d.cash > 0 ? formatCompactValue(d.cash) : "",
        revenueLabel: d.revenue > 0 ? formatCompactValue(d.revenue) : "",
      }));
    }

    const step = Math.max(1, Math.floor(data.length / 6)); // Cumulative: ~6 labels max

    let cashSum = 0;
    let revenueSum = 0;
    return data.map((d, i) => {
      cashSum += d.cash;
      revenueSum += d.revenue;
      const showLabel = i % step === 0 || i === data.length - 1;
      return {
        ...d,
        cash: cashSum,
        revenue: revenueSum,
        label: showLabel && cashSum > 0 ? formatCompactValue(cashSum) : "",
        revenueLabel: showLabel && revenueSum > 0 ? formatCompactValue(revenueSum) : "",
      };
    });
  }, [data, mode, currencyMode]);

  // ALL HOOKS MUST BE ABOVE ANY EARLY RETURN — React rules of hooks.
  // Compute chart dimensions and scales (hooks first, conditionals after)
  const svgWidth = dims.width;
  const svgHeight = dims.height;
  const chartWidth = svgWidth - PADDING.left - PADDING.right;
  const chartHeight = svgHeight - PADDING.top - PADDING.bottom;

  // Compute Y domain from visible series
  const { yMin, yMax, canRender } = useMemo(() => {
    if (
      !data ||
      data.length === 0 ||
      svgWidth <= 0 ||
      svgHeight <= 0 ||
      chartWidth <= 0 ||
      chartHeight <= 0
    ) {
      return { yMin: 0, yMax: 1, canRender: false };
    }
    const allValues: number[] = [];
    if (canShowCash && cashVisible) chartData.forEach((d) => allValues.push(d.cash));
    if (canShowRevenue && revenueVisible) chartData.forEach((d) => allValues.push(d.revenue));
    if (allValues.length === 0) allValues.push(0, 1);
    const rawMax = Math.max(...allValues);
    const rawMin = 0;
    const yRange = rawMax - rawMin || 1;
    return {
      yMin: rawMin,
      yMax: rawMax + yRange * 0.12,
      canRender: true,
    };
  }, [
    data,
    svgWidth,
    svgHeight,
    chartWidth,
    chartHeight,
    canShowCash,
    cashVisible,
    canShowRevenue,
    revenueVisible,
    chartData,
  ]);

  const xScale = useCallback(
    (index: number) => {
      if (chartData.length <= 1) return PADDING.left + chartWidth / 2;
      return PADDING.left + (index / (chartData.length - 1)) * chartWidth;
    },
    [chartData.length, chartWidth]
  );

  const yScale = useCallback(
    (value: number) => {
      return PADDING.top + (1 - (value - yMin) / (yMax - yMin)) * chartHeight;
    },
    [yMin, yMax, chartHeight]
  );

  // Catmull-Rom style smooth curve via SVG cubic bezier
  const buildSmoothLinePath = (points: { x: number; y: number }[]): string => {
    if (points.length === 0) return "";
    if (points.length === 1) return `M${points[0].x},${points[0].y}`;
    let d = `M${points[0].x.toFixed(1)},${points[0].y.toFixed(1)}`;
    for (let i = 0; i < points.length - 1; i++) {
      const p0 = points[Math.max(0, i - 1)];
      const p1 = points[i];
      const p2 = points[i + 1];
      const p3 = points[Math.min(points.length - 1, i + 2)];
      const cp1x = p1.x + (p2.x - p0.x) / 6;
      const cp1y = p1.y + (p2.y - p0.y) / 6;
      const cp2x = p2.x - (p3.x - p1.x) / 6;
      const cp2y = p2.y - (p3.y - p1.y) / 6;
      d += ` C${cp1x.toFixed(1)},${cp1y.toFixed(1)} ${cp2x.toFixed(1)},${cp2y.toFixed(1)} ${p2.x.toFixed(1)},${p2.y.toFixed(1)}`;
    }
    return d;
  };

  // Smooth area path (line + baseline)
  const buildSmoothAreaPath = (points: { x: number; y: number }[]): string => {
    if (points.length === 0) return "";
    const baseline = PADDING.top + chartHeight;
    const line = buildSmoothLinePath(points);
    const close = ` L${points[points.length - 1].x.toFixed(1)},${baseline} L${points[0].x.toFixed(1)},${baseline} Z`;
    return line + close;
  };

  // Compute point arrays
  const cashPoints = canRender
    ? chartData.map((d, i) => ({ x: xScale(i), y: yScale(d.cash) }))
    : [];
  const revenuePoints = canRender
    ? chartData.map((d, i) => ({ x: xScale(i), y: yScale(d.revenue) }))
    : [];

  // Which indices get a data label. Daily mode thins to one peak per pixel slot so wide
  // ranges stay legible; cumulative mode already thins its label strings to ~6, so we pass
  // it through untouched (null = "no extra gating, honor the label string").
  const cashLabelIndices =
    canRender && mode === "daily" && canShowCash && cashVisible
      ? pickPeakLabelIndices(
          cashPoints,
          chartData.map((d) => d.cash)
        )
      : null;
  const revenueLabelIndices =
    canRender && mode === "daily" && canShowRevenue && revenueVisible
      ? pickPeakLabelIndices(
          revenuePoints,
          chartData.map((d) => d.revenue)
        )
      : null;

  // X-axis ticks — thin to ~10 labels
  const xTickInterval = Math.max(1, Math.ceil(chartData.length / 10));
  const xTickIndices = canRender
    ? chartData.reduce<number[]>((acc, _, i) => {
        if (i % xTickInterval === 0 || i === chartData.length - 1) acc.push(i);
        return acc;
      }, [])
    : [];

  // Y-axis ticks — ~5 ticks
  const yTickCount = 5;
  const yTickValues = canRender
    ? Array.from({ length: yTickCount }, (_, i) => {
        return yMin + ((yMax - yMin) * i) / (yTickCount - 1);
      })
    : [];

  // Mouse interaction: find nearest data index from SVG X coordinate
  const handleMouseMove = useCallback(
    (e: React.MouseEvent<SVGRectElement>) => {
      if (!canRender || chartData.length === 0) return;
      const rect = e.currentTarget.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;
      // Map mouse X back to data index
      const clampedX = Math.max(PADDING.left, Math.min(mouseX, PADDING.left + chartWidth));
      const ratio = (clampedX - PADDING.left) / chartWidth;
      const rawIndex = ratio * (chartData.length - 1);
      const index = Math.round(rawIndex);
      const safeIndex = Math.max(0, Math.min(index, chartData.length - 1));
      const pointX = xScale(safeIndex);
      setTooltip({ visible: true, x: pointX, y: 0, index: safeIndex });
    },
    [canRender, chartData.length, chartWidth, xScale]
  );

  const handleMouseLeave = useCallback(() => {
    setTooltip({ visible: false, x: 0, y: 0, index: -1 });
  }, []);

  // Tooltip content
  const tooltipData = tooltip.visible && tooltip.index >= 0 ? chartData[tooltip.index] : null;
  // Compute tooltip left position (keep within bounds)
  const tooltipPixelX = tooltip.x;
  const tooltipWidth = 160;
  // Offset from the line; flip if near right edge
  const tooltipLeft =
    tooltipPixelX + tooltipWidth + 8 > svgWidth
      ? tooltipPixelX - tooltipWidth - 8
      : tooltipPixelX + 8;

  // Empty state — rendered AFTER all hooks (React rules of hooks: no early returns before hooks)
  if (!data || data.length === 0) {
    return (
      <div className="liquid-glass p-3 sm:p-5 h-full min-h-[230px] flex flex-col items-center justify-center gap-3">
        <svg
          className="w-10 h-10 text-glass-muted opacity-40"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={1.5}
            d="M3 13.125C3 12.504 3.504 12 4.125 12h2.25c.621 0 1.125.504 1.125 1.125v6.75C7.5 20.496 6.996 21 6.375 21h-2.25A1.125 1.125 0 013 19.875v-6.75zM9.75 8.625c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125v11.25c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V8.625zM16.5 4.125c0-.621.504-1.125 1.125-1.125h2.25C20.496 3 21 3.504 21 4.125v15.75c0 .621-.504 1.125-1.125 1.125h-2.25a1.125 1.125 0 01-1.125-1.125V4.125z"
          />
        </svg>
        <p className="text-sm text-glass-muted">No revenue data for this period</p>
        <p className="text-xs text-glass-inactive">Data appears once transactions are recorded</p>
      </div>
    );
  }

  return (
    <div className="liquid-glass anim-lift p-3 sm:p-5 h-full min-h-[230px] flex flex-col overflow-hidden">
      <div className="flex flex-wrap items-center gap-2 sm:gap-4 mb-3 sm:mb-4 flex-shrink-0">
        <h3 className="text-xs sm:text-sm font-semibold text-glass-secondary uppercase tracking-wider">
          Cash & Revenue
        </h3>

        {/* Mode toggle */}
        <div className="flex items-center liquid-glass-light rounded-lg p-0.5 text-xs font-medium">
          <button
            onClick={() => setMode("daily")}
            className={`px-2.5 py-1 rounded-md transition-all anim-tap ${
              mode === "daily" ? "text-glass" : "text-glass-muted hover:text-glass-secondary"
            }`}
            style={{
              background: mode === "daily" ? "var(--glass-bg-active)" : "transparent",
              boxShadow: mode === "daily" ? "var(--glass-shadow-inset)" : "none",
            }}
            title={
              granularity === "daily"
                ? "Per-day totals"
                : `Per-period totals (rolled up to ${granularity} for this range)`
            }
          >
            {GRANULARITY_LABEL[granularity]}
          </button>
          <button
            onClick={() => setMode("cumulative")}
            className={`px-2.5 py-1 rounded-md transition-all anim-tap ${
              mode === "cumulative" ? "text-glass" : "text-glass-muted hover:text-glass-secondary"
            }`}
            style={{
              background: mode === "cumulative" ? "var(--glass-bg-active)" : "transparent",
              boxShadow: mode === "cumulative" ? "var(--glass-shadow-inset)" : "none",
            }}
          >
            Cumulative
          </button>
        </div>

        {/* Clickable legend toggles - styled like Daily/Cumulative */}
        <div className="flex items-center liquid-glass-light rounded-lg p-0.5 text-xs font-medium ml-auto">
          {canShowCash && (
            <button
              onClick={() => setCashVisible((v) => !v)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all anim-tap ${
                cashVisible ? "text-glass" : "text-glass-muted"
              }`}
              style={{
                background: cashVisible ? "var(--glass-bg-active)" : "transparent",
                boxShadow: cashVisible ? "var(--glass-shadow-inset)" : "none",
              }}
              title={cashVisible ? "Hide cash" : "Show cash"}
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  background: CHART_COLORS_HEX.cash,
                  boxShadow: cashVisible
                    ? `0 0 6px ${hexToRgba(CHART_COLORS_HEX.cash, 0.6)}`
                    : "none",
                  opacity: cashVisible ? 1 : 0.4,
                }}
              />
              <span className="token-10 sm:text-xs">Cash</span>
            </button>
          )}
          {canShowRevenue && (
            <button
              onClick={() => setRevenueVisible((v) => !v)}
              className={`flex items-center gap-1.5 px-2.5 py-1 rounded-md transition-all anim-tap ${
                revenueVisible ? "text-glass" : "text-glass-muted"
              }`}
              style={{
                background: revenueVisible ? "var(--glass-bg-active)" : "transparent",
                boxShadow: revenueVisible ? "var(--glass-shadow-inset)" : "none",
              }}
              title={revenueVisible ? "Hide revenue" : "Show revenue"}
            >
              <div
                className="w-2 h-2 rounded-full"
                style={{
                  background: CHART_COLORS_HEX.revenue,
                  boxShadow: revenueVisible
                    ? `0 0 6px ${hexToRgba(CHART_COLORS_HEX.revenue, 0.6)}`
                    : "none",
                  opacity: revenueVisible ? 1 : 0.4,
                }}
              />
              <span className="token-10 sm:text-xs">Revenue</span>
            </button>
          )}
        </div>
      </div>

      {/* Chart container */}
      <div className="relative flex-1 min-h-0" style={{ minHeight: 200 }}>
        <div
          ref={setContainerNode}
          key={`reveal-${cashVisible}-${revenueVisible}`}
          className="chart-reveal"
          style={{ width: "100%", height: "100%", position: "relative" }}
        >
          {canRender && (
            <>
              <svg
                width={svgWidth}
                height={svgHeight}
                viewBox={`0 0 ${svgWidth} ${svgHeight}`}
                style={{ display: "block", overflow: "visible" }}
              >
                <defs>
                  {/* Cash gradient */}
                  <linearGradient id="cashGradientSVG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS_HEX.cash} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={CHART_COLORS_HEX.cash} stopOpacity={0.02} />
                  </linearGradient>
                  {/* Revenue gradient */}
                  <linearGradient id="revenueGradientSVG" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={CHART_COLORS_HEX.revenue} stopOpacity={0.2} />
                    <stop offset="100%" stopColor={CHART_COLORS_HEX.revenue} stopOpacity={0.03} />
                  </linearGradient>
                </defs>

                {/* Y-axis ticks and labels */}
                {yTickValues.map((val, i) => {
                  const y = yScale(val);
                  const cv = toDisplayCurrency(val); // exec USD->AUD toggle
                  const label = cv >= 1000 ? `${(cv / 1000).toFixed(0)}K` : `${Math.round(cv)}`;
                  return (
                    <g key={`ytick-${i}`}>
                      {/* Grid line */}
                      <line
                        x1={PADDING.left}
                        y1={y}
                        x2={PADDING.left + chartWidth}
                        y2={y}
                        stroke="var(--glass-border)"
                        strokeOpacity={0.3}
                        strokeWidth={1}
                        strokeDasharray="3 3"
                      />
                      {/* Y label */}
                      <text
                        x={PADDING.left - 6}
                        y={y}
                        textAnchor="end"
                        dominantBaseline="middle"
                        fontSize={11}
                        fill="var(--glass-text-muted)"
                        fontFamily="'DM Mono', monospace"
                      >
                        {label}
                      </text>
                    </g>
                  );
                })}

                {/* X-axis labels */}
                {xTickIndices.map((i) => {
                  const x = xScale(i);
                  return (
                    <text
                      key={`xtick-${i}`}
                      x={x}
                      y={PADDING.top + chartHeight + 16}
                      textAnchor="middle"
                      fontSize={11}
                      fill="var(--glass-text-muted)"
                      fontFamily="'DM Mono', monospace"
                    >
                      {formatDateLabel(chartData[i].date)}
                    </text>
                  );
                })}

                {/* Revenue area (back layer) */}
                {canShowRevenue && revenueVisible && (
                  <>
                    <path
                      d={buildSmoothAreaPath(revenuePoints)}
                      fill="url(#revenueGradientSVG)"
                      stroke="none"
                    />
                    <path
                      d={buildSmoothLinePath(revenuePoints)}
                      fill="none"
                      stroke={CHART_COLORS_HEX.revenue}
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {/* Revenue data labels */}
                    {chartData.map((d, i) => {
                      if (!d.revenueLabel) return null;
                      if (i === 0) return null;
                      // Daily mode: only label the per-slot peak (skip the rest).
                      if (revenueLabelIndices && !revenueLabelIndices.has(i)) return null;
                      // Skip revenue label if revenue <= cash and both visible (cash label takes it)
                      if (canShowCash && cashVisible) {
                        if (d.revenue <= d.cash) return null;
                        const maxVal = Math.max(d.cash, d.revenue);
                        if (maxVal > 0 && Math.abs(d.revenue - d.cash) / maxVal < 0.6) return null;
                      }
                      const px = revenuePoints[i].x;
                      const py = revenuePoints[i].y;
                      return (
                        <text
                          key={`rlabel-${i}`}
                          x={px}
                          y={py - 12}
                          textAnchor="middle"
                          fontSize={11}
                          fontWeight={500}
                          fill={CHART_COLORS_HEX.revenue}
                          fontFamily="'DM Mono', monospace"
                          paintOrder="stroke"
                          stroke="var(--surface)"
                          strokeWidth={3}
                          strokeLinejoin="round"
                        >
                          {d.revenueLabel}
                        </text>
                      );
                    })}
                  </>
                )}

                {/* Cash area (front layer) */}
                {canShowCash && cashVisible && (
                  <>
                    <path
                      d={buildSmoothAreaPath(cashPoints)}
                      fill="url(#cashGradientSVG)"
                      stroke="none"
                    />
                    <path
                      d={buildSmoothLinePath(cashPoints)}
                      fill="none"
                      stroke={CHART_COLORS_HEX.cash}
                      strokeWidth={1.5}
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                    {/* Cash data labels */}
                    {chartData.map((d, i) => {
                      if (!d.label) return null;
                      if (i === 0) return null;
                      // Daily mode: only label the per-slot peak (skip the rest).
                      if (cashLabelIndices && !cashLabelIndices.has(i)) return null;
                      const px = cashPoints[i].x;
                      const py = cashPoints[i].y;
                      return (
                        <text
                          key={`clabel-${i}`}
                          x={px}
                          y={py - 18}
                          textAnchor="middle"
                          fontSize={11}
                          fontWeight={500}
                          fill={CHART_COLORS_HEX.cash}
                          fontFamily="'DM Mono', monospace"
                          paintOrder="stroke"
                          stroke="var(--surface)"
                          strokeWidth={3}
                          strokeLinejoin="round"
                        >
                          {d.label}
                        </text>
                      );
                    })}
                  </>
                )}

                {/* Tooltip crosshair line */}
                {tooltip.visible && tooltip.index >= 0 && (
                  <line
                    x1={xScale(tooltip.index)}
                    y1={PADDING.top}
                    x2={xScale(tooltip.index)}
                    y2={PADDING.top + chartHeight}
                    stroke="var(--glass-text-muted)"
                    strokeOpacity={0.4}
                    strokeWidth={1}
                    strokeDasharray="4 2"
                  />
                )}

                {/* Invisible overlay rect for mouse tracking */}
                <rect
                  x={PADDING.left}
                  y={PADDING.top}
                  width={chartWidth}
                  height={chartHeight}
                  fill="transparent"
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  style={{ cursor: "crosshair" }}
                />
              </svg>

              {/* Tooltip — HTML div for better styling */}
              {tooltip.visible && tooltipData && (
                <div
                  style={{
                    position: "absolute",
                    top: PADDING.top + chartHeight * 0.1,
                    left: tooltipLeft,
                    pointerEvents: "none",
                    zIndex: 10,
                    background: "var(--tt-chart-bg)",
                    border: "var(--tt-chart-border)",
                    borderRadius: "var(--tt-radius)",
                    color: "var(--tt-chart-text)",
                    padding: "10px 14px",
                    minWidth: tooltipWidth,
                  }}
                >
                  <p
                    style={{
                      color: "var(--tt-chart-text-muted)",
                      fontSize: 12,
                      marginBottom: 6,
                      fontWeight: 500,
                    }}
                  >
                    {formatDateLabel(tooltipData.date)}
                  </p>
                  {canShowRevenue && revenueVisible && (
                    <p
                      style={{
                        color: CHART_COLORS_HEX.revenue,
                        fontSize: 13,
                        marginBottom: 2,
                      }}
                    >
                      Revenue Closed: $
                      {toDisplayCurrency(Number(tooltipData.revenue || 0)).toLocaleString()}
                    </p>
                  )}
                  {canShowCash && cashVisible && (
                    <p style={{ color: CHART_COLORS_HEX.cash, fontSize: 13 }}>
                      Cash Collected: $
                      {toDisplayCurrency(Number(tooltipData.cash || 0)).toLocaleString()}
                    </p>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(FinancialGraph);
